import { CHART_BARCHART_OPTIONS, CHART_COLOR_2 } from "configApp";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "store/hooks";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";

interface ProductModalContentProps {
    data?: any
}


const ProductModalContent3: React.FC<ProductModalContentProps> = ({ data }) => {
    const { t } = useTranslation();
    const [, setWindowSize] = useState<number>(); // NOSONAR
    const projectStore = useAppSelector((state) => state.project);
    const { network } = useSalesNetwork();

    const barChartOptions = { ...CHART_BARCHART_OPTIONS, ...{ colors: CHART_COLOR_2 } };

    const graphNrj = network?._parameters?.GRAPH_NRJ;
    const showGas = !graphNrj || graphNrj.split(',').includes('1');
    const showOil = !graphNrj || graphNrj.split(',').includes('2');
    const showPropane = !graphNrj || graphNrj.split(',').includes('3');

    const graphNrj2 = network?._parameters?.GRAPH_NRJ_2;
    const showChartConso = !graphNrj2 || graphNrj2.split(',').includes('1');
    const showChartCost = !graphNrj2 || graphNrj2.split(',').includes('2');
    const showChartCo2 = !graphNrj2 || graphNrj2.split(',').includes('3');

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        // Fix a bug on smartphone to adapt first chart to screen size
        setTimeout(() => {
            setWindowSize(window.innerWidth);
        }, 100)
    }, []);

    const handleResize = useCallback(() => {
        // @ts-ignore
        delete barChartOptions.animation;
        setWindowSize(window.innerWidth);
    }, []);

    useEffect(() => {
        return () => {
            window.removeEventListener("resize", handleResize);
        }
    }, [handleResize]);

    const drawChartConso = useCallback((conso: any) => {

        const filterZeroOnlySeries = (rows: any[]) => {
            if (!rows?.length) return { rows, keptSeries: [] as number[] };

            const header = rows[0];
            const hasAnnotation = typeof header[header.length - 1] === 'object' && header[header.length - 1]?.role === 'annotation';
            const seriesStart = 1;
            const seriesEnd = hasAnnotation ? header.length - 2 : header.length - 1;
            const keptSeries: number[] = [];

            for (let col = seriesStart; col <= seriesEnd; col++) {
                const hasNonZero = rows.slice(1).some((r: any[]) => {
                    const value = Number(r[col] ?? 0);
                    return Number.isFinite(value) && value !== 0;
                });
                if (hasNonZero) keptSeries.push(col);
            }

            // Keep original data if everything is zero to avoid drawing errors.
            if (!keptSeries.length) return { rows, keptSeries: [] as number[] };

            const filteredHeader = [
                header[0],
                ...keptSeries.map((col) => header[col]),
                ...(hasAnnotation ? [header[header.length - 1]] : []),
            ];

            const filteredRows = rows.slice(1).map((r: any[]) => [
                r[0],
                ...keptSeries.map((col) => r[col]),
                ...(hasAnnotation ? [r[r.length - 1]] : []),
            ]);

            return { rows: [filteredHeader, ...filteredRows], keptSeries };
        };

        const getChartOptionsWithFilteredColors = (keptSeries: number[]) => {
            if (!keptSeries.length) return barChartOptions;
            const sourceColors = Array.isArray((barChartOptions as any).colors) ? (barChartOptions as any).colors : [];
            return {
                ...barChartOptions,
                colors: keptSeries.map((col) => sourceColors[col - 1]).filter(Boolean),
            };
        };

        const chartData = [
            projectStore.project.simulationCool
                ? [t('ENERGY'), t('HEATING'), t('HOT_WATER'), t('RAF'), { role: 'annotation', type: "number" }]
                : [t('ENERGY'), t('HEATING'), t('HOT_WATER'), { role: 'annotation', type: "number" }]
        ];

        chartData.push(projectStore.project.simulationCool
            ? [data?.Product || t('HEATPUMP'), conso.HPHeatingKWH, conso.HPDHWKWH, conso.RAFHPKWH, conso.HPHeatingKWH + conso.HPDHWKWH + conso.RAFHPKWH]
            : [data?.Product || t('HEATPUMP'), conso.HPHeatingKWH, conso.HPDHWKWH, conso.HPHeatingKWH + conso.HPDHWKWH]);
        if (showGas) {
            chartData.push(projectStore.project.simulationCool
                ? [t('GAS'), conso.GasHeatingKWH, conso.GasDHWKWH, 0, conso.GasHeatingKWH + conso.GasDHWKWH]
                : [t('GAS'), conso.GasHeatingKWH, conso.GasDHWKWH, conso.GasHeatingKWH + conso.GasDHWKWH]);
        }
        if (showOil) {
            chartData.push(projectStore.project.simulationCool
                ? [t('OIL'), conso.OilHeatingKHW, conso.OilDHWKHW, 0, conso.OilHeatingKHW + conso.OilDHWKHW]
                : [t('OIL'), conso.OilHeatingKHW, conso.OilDHWKHW, conso.OilHeatingKHW + conso.OilDHWKHW]);
        }
        if (showPropane) {
            chartData.push(projectStore.project.simulationCool
                ? [t('PROPANE'), conso.PropaneHeatingKWH, conso.PropaneDHWKWH, 0, conso.PropaneHeatingKWH + conso.PropaneDHWKWH]
                : [t('PROPANE'), conso.PropaneHeatingKWH, conso.PropaneDHWKWH, conso.PropaneHeatingKWH + conso.PropaneDHWKWH]);
        }

        const filtered = filterZeroOnlySeries(chartData);
        const dynamicBarChartOptions = getChartOptionsWithFilteredColors(filtered.keptSeries);

        // @ts-ignore
        const _data = new window.google.visualization.arrayToDataTable(filtered.rows);

        // @ts-ignore
        const chart = new window.google.visualization.ColumnChart(document.getElementById('chartConso'));

        // find v-axis max value
        let vAxisMax = null;
        for (let i = 1; i < _data.getNumberOfColumns(); i++) {
            const range = _data.getColumnRange(i);
            vAxisMax = Math.max(vAxisMax || range.max, range.max);
        }
        // add buffer for annotation
        // @ts-ignore
        vAxisMax *= 1.2;

        chart.draw(_data, {
            ...dynamicBarChartOptions, ...{
                title: t('BARCHART_CONSO_AN_TITLE'),
                vAxis: {
                    title: t('CONSUMED_ENERGY'),
                    titleTextStyle: { italic: false },
                    viewWindow: {
                        max: vAxisMax
                    }
                }
            }
        });
    }, []);

    const drawChartCost = (conso: any) => {

        const filterZeroOnlySeries = (rows: any[]) => {
            if (!rows?.length) return { rows, keptSeries: [] as number[] };

            const header = rows[0];
            const hasAnnotation = typeof header[header.length - 1] === 'object' && header[header.length - 1]?.role === 'annotation';
            const seriesStart = 1;
            const seriesEnd = hasAnnotation ? header.length - 2 : header.length - 1;
            const keptSeries: number[] = [];

            for (let col = seriesStart; col <= seriesEnd; col++) {
                const hasNonZero = rows.slice(1).some((r: any[]) => {
                    const value = Number(r[col] ?? 0);
                    return Number.isFinite(value) && value !== 0;
                });
                if (hasNonZero) keptSeries.push(col);
            }

            if (!keptSeries.length) return { rows, keptSeries: [] as number[] };

            const filteredHeader = [
                header[0],
                ...keptSeries.map((col) => header[col]),
                ...(hasAnnotation ? [header[header.length - 1]] : []),
            ];

            const filteredRows = rows.slice(1).map((r: any[]) => [
                r[0],
                ...keptSeries.map((col) => r[col]),
                ...(hasAnnotation ? [r[r.length - 1]] : []),
            ]);

            return { rows: [filteredHeader, ...filteredRows], keptSeries };
        };

        const getChartOptionsWithFilteredColors = (keptSeries: number[]) => {
            if (!keptSeries.length) return barChartOptions;
            const sourceColors = Array.isArray((barChartOptions as any).colors) ? (barChartOptions as any).colors : [];
            return {
                ...barChartOptions,
                colors: keptSeries.map((col) => sourceColors[col - 1]).filter(Boolean),
            };
        };

        const chartData = [
            projectStore.project.simulationCool
                ? [t('ENERGY'), t('HEATING'), t('HOT_WATER'), t('RAF'), { role: 'annotation' }]
                : [t('ENERGY'), t('HEATING'), t('HOT_WATER'), { role: 'annotation' }]
            //['PAC', 10, 24, 20, ''],
        ];

        chartData.push(projectStore.project.simulationCool
            ? [data?.Product || t('HEATPUMP'), conso.HPHeatingCost, conso.HPDHWCost, conso.RAFHPCost, conso.HPHeatingCost + conso.HPDHWCost + conso.RAFHPCost]
            : [data?.Product || t('HEATPUMP'), conso.HPHeatingCost, conso.HPDHWCost, conso.HPHeatingCost + conso.HPDHWCost]);
        if (showGas) {
            chartData.push(projectStore.project.simulationCool
                ? [t('GAS'), conso.GasHeatingCost, conso.GasDHWCost, 0, conso.GasHeatingCost + conso.GasDHWCost]
                : [t('GAS'), conso.GasHeatingCost, conso.GasDHWCost, conso.GasHeatingCost + conso.GasDHWCost]);
        }
        if (showOil) {
            chartData.push(projectStore.project.simulationCool
                ? [t('OIL'), conso.OilHeatingCost, conso.OilDHWCost, 0, conso.OilHeatingCost + conso.OilDHWCost]
                : [t('OIL'), conso.OilHeatingCost, conso.OilDHWCost, conso.OilHeatingCost + conso.OilDHWCost]);
        }
        if (showPropane) {
            chartData.push(projectStore.project.simulationCool
                ? [t('PROPANE'), conso.PropaneHeatingCost, conso.PropaneDHWCost, 0, conso.PropaneHeatingCost + conso.PropaneDHWCost]
                : [t('PROPANE'), conso.PropaneHeatingCost, conso.PropaneDHWCost, conso.PropaneHeatingCost + conso.PropaneDHWCost]);
        }

        const filtered = filterZeroOnlySeries(chartData);
        const dynamicBarChartOptions = getChartOptionsWithFilteredColors(filtered.keptSeries);

        // @ts-ignore
        const _data = new window.google.visualization.arrayToDataTable(filtered.rows);

        // @ts-ignore
        const chart = new window.google.visualization.ColumnChart(document.getElementById('chartCost'));

        // find v-axis max value
        let vAxisMax = null;
        for (let i = 1; i < _data.getNumberOfColumns(); i++) {
            const range = _data.getColumnRange(i);
            vAxisMax = Math.max(vAxisMax || range.max, range.max);
        }
        // add buffer for annotation
        // @ts-ignore
        vAxisMax *= 1.2;

        chart.draw(_data, {
            ...dynamicBarChartOptions, ...{
                title: t('BARCHART_COST_AN_TITLE', { currency: network?._parameters.currency }),
                vAxis: {
                    title: t('ANNUAL_COST', { currency: network?._parameters.currency }),
                    titleTextStyle: { italic: false },
                    viewWindow: {
                        max: vAxisMax
                    }
                }
            }
        });
    };

    const drawChartCo2 = (conso: any) => {
        const filterZeroOnlySeries = (rows: any[]) => {
            if (!rows?.length) return { rows, keptSeries: [] as number[] };

            const header = rows[0];
            const hasAnnotation = typeof header[header.length - 1] === 'object' && header[header.length - 1]?.role === 'annotation';
            const seriesStart = 1;
            const seriesEnd = hasAnnotation ? header.length - 2 : header.length - 1;
            const keptSeries: number[] = [];

            for (let col = seriesStart; col <= seriesEnd; col++) {
                const hasNonZero = rows.slice(1).some((r: any[]) => {
                    const value = Number(r[col] ?? 0);
                    return Number.isFinite(value) && value !== 0;
                });
                if (hasNonZero) keptSeries.push(col);
            }

            if (!keptSeries.length) return { rows, keptSeries: [] as number[] };

            const filteredHeader = [
                header[0],
                ...keptSeries.map((col) => header[col]),
                ...(hasAnnotation ? [header[header.length - 1]] : []),
            ];

            const filteredRows = rows.slice(1).map((r: any[]) => [
                r[0],
                ...keptSeries.map((col) => r[col]),
                ...(hasAnnotation ? [r[r.length - 1]] : []),
            ]);

            return { rows: [filteredHeader, ...filteredRows], keptSeries };
        };

        const getChartOptionsWithFilteredColors = (keptSeries: number[]) => {
            if (!keptSeries.length) return barChartOptions;
            const sourceColors = Array.isArray((barChartOptions as any).colors) ? (barChartOptions as any).colors : [];
            return {
                ...barChartOptions,
                colors: keptSeries.map((col) => sourceColors[col - 1]).filter(Boolean),
            };
        };

        const chartData = [
            projectStore.project.simulationCool
                ? [t('ENERGY'), t('HEATING'), t('HOT_WATER'), t('RAF'), { role: 'annotation' }]
                : [t('ENERGY'), t('HEATING'), t('HOT_WATER'), { role: 'annotation' }]
            //['PAC', 10, 24, 20, ''],
        ];

        chartData.push(projectStore.project.simulationCool
            ? [data?.Product || t('HEATPUMP'), conso.HPHeatingCO2, conso.HPDHWCO2, conso.RAFHPCO2, conso.HPHeatingCO2 + conso.HPDHWCO2 + conso.RAFHPCO2]
            : [data?.Product || t('HEATPUMP'), conso.HPHeatingCO2, conso.HPDHWCO2, conso.HPHeatingCO2 + conso.HPDHWCO2]);
        if (showGas) {
            chartData.push(projectStore.project.simulationCool
                ? [t('GAS'), conso.GasHeatingCO2, conso.GasDHWCO2, 0, conso.GasHeatingCO2 + conso.GasDHWCO2]
                : [t('GAS'), conso.GasHeatingCO2, conso.GasDHWCO2, conso.GasHeatingCO2 + conso.GasDHWCO2]);
        }
        if (showOil) {
            chartData.push(projectStore.project.simulationCool
                ? [t('OIL'), conso.OilHeatingCO2, conso.OilDHWCO2, 0, conso.OilHeatingCO2 + conso.OilDHWCO2]
                : [t('OIL'), conso.OilHeatingCO2, conso.OilDHWCO2, conso.OilHeatingCO2 + conso.OilDHWCO2]);
        }
        if (showPropane) {
            chartData.push(projectStore.project.simulationCool
                ? [t('PROPANE'), conso.PropaneHeatingCO2, conso.PropaneDHWCO2, 0, conso.PropaneHeatingCO2 + conso.PropaneDHWCO2]
                : [t('PROPANE'), conso.PropaneHeatingCO2, conso.PropaneDHWCO2, conso.PropaneHeatingCO2 + conso.PropaneDHWCO2]);
        }

        const filtered = filterZeroOnlySeries(chartData);
        const dynamicBarChartOptions = getChartOptionsWithFilteredColors(filtered.keptSeries);

        // @ts-ignore
        const _data = new window.google.visualization.arrayToDataTable(filtered.rows);

        // @ts-ignore
        const chart = new window.google.visualization.ColumnChart(document.getElementById('chartCo2'));

        // find v-axis max value
        let vAxisMax = null;
        for (let i = 1; i < _data.getNumberOfColumns(); i++) {
            const range = _data.getColumnRange(i);
            vAxisMax = Math.max(vAxisMax || range.max, range.max);
        }
        // add buffer for annotation
        // @ts-ignore
        vAxisMax *= 1.2;

        chart.draw(_data, {
            ...dynamicBarChartOptions, ...{
                title: t('BARCHART_CO2_AN_TITLE'),
                vAxis: {
                    title: t('CO2_EMISSIONS', { an: t('YEAR_UNIT') }),
                    titleTextStyle: { italic: false },
                    viewWindow: {
                        max: vAxisMax
                    }
                }
            }
        });
    };

    // const toggle = () => { // NOSONAR
    //   setModalIsOpen(!modalIsOpen) // NOSONAR
    // } // NOSONAR

    // google.charts.load('current', {packages: ['corechart'], language: UIStore.lang}) // NOSONAR
    // google.charts.setOnLoadCallback(() => drawChartConso(product.E4)) // NOSONAR
    // google.charts.setOnLoadCallback(() => drawChartCost(product.E4)) // NOSONAR
    // google.charts.setOnLoadCallback(() => drawChartCo2(product.E4)) // NOSONAR

    useEffect(() => {
        if (showChartConso) drawChartConso(data.E4); // NOSONAR
        if (showChartCost) drawChartCost(data.E4); // NOSONAR
        if (showChartCo2) drawChartCo2(data.E4); // NOSONAR
    }, [showChartConso, showChartCost, showChartCo2, data.E4]);

    return (
        <>
            <group
                //data-width="auto"
                data-gap="30"
                data-text-size="medium"
                data-space-vertical="30"
                data-space-horizontal="50"
                data-direction="column"
                data-justify="start"

            >


                {showChartConso && (
                    <group data-height="450"
                        //data-background="grey-color-light"
                        data-name="chart-holder"
                        data-contain="visible"
                    >
                        <group id="chartConso" data-min-length="400" />
                    </group>
                )}
                {showChartConso && (showChartCost || showChartCo2) && <space></space>}
                {showChartCost && (
                    <group data-height="450"
                        //data-background="grey-color-light"
                        data-name="chart-holder"
                        data-contain="visible"
                    >
                        <group id="chartCost" data-min-length="400" />
                    </group>
                )}
                {showChartCost && showChartCo2 && <space></space>}
                {showChartCo2 && (
                    <group data-height="450"
                        //data-background="grey-color-light"
                        data-name="chart-holder"
                        data-contain="visible"
                    >
                        <group id="chartCo2" data-min-length="400" />
                    </group>
                )}
            </group>
        </>
    )
}

export default ProductModalContent3;